import { memo, useCallback, useMemo } from 'react';

import { type PublicClip } from '@air/api';
import { type PublicBoard } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';

import { PublicBoardNullState } from '~/components/PublicBoard/PublicBoardNullState';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { type TableViewProps } from '~/components/TableView';
import { CustomFieldTableHeader } from '~/components/TableView/CustomFieldTableHeader/CustomFieldTableHeader';
import { SelectableTableView, type SelectableTableViewProps } from '~/components/TableView/SelectableTableView';
import { TableHeaders } from '~/components/TableView/TableHeaders';
import { TablePageContainer } from '~/components/TableView/TablePageContainer';
import { TableViewProvider } from '~/components/TableView/TableViewContext';
import { usePublicTableRows } from '~/components/TableView/usePublicTableRows';
import { useTableSelectableItems } from '~/components/TableView/useTableSelectableItems';
import { useTableHeaderPositionContext } from '~/components/v2/components/shared/table/TableHeaderPositionContextProvider';
import { useWorkspaceHorizontalPadding } from '~/hooks/useWorkspaceHorizontalPadding';
import { type UseGalleryMixedData } from '~/swr-hooks/gallery/types';
import { canChangeBoardCustomFields } from '~/utils/permissions/boardPermissions';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export type PublicBoardTableViewProps = Pick<SelectableTableViewProps, 'scrollElementRef'> & {
  /**
   * The board's items, fetched by the hosting surface's own table view (usePublicTableViewItems
   * with that surface's fetch scope) — this shared body owns the table rendering, never the
   * fetch scope.
   */
  itemsData: UseGalleryMixedData<PublicBoard, PublicClip>;
};

export const PublicBoardTableView = memo(({ itemsData, scrollElementRef }: PublicBoardTableViewProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { tableHeaderTopPosition } = useTableHeaderPositionContext();
  const { responsiveHorizontalPadding } = useWorkspaceHorizontalPadding();
  const { data, hasMore, isEmpty, isLoading, isLoadingMore, loadNextPage } = itemsData;

  const { permissions } = usePublicPermissions();
  const canEditCustomFields = canChangeBoardCustomFields(permissions);
  const canDownload = canGenerateZip(permissions);

  /**
   * If the parent shortUrl has the `asset.canViewOriginal` or `customField.canManageApplications` permission,
   * the user can select all items (assets or boards) in the table.
   */
  const canSelectItems = canDownload || canEditCustomFields;

  const rows = usePublicTableRows({
    data,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore: loadNextPage,
    isAssetSelectable: canSelectItems,
    isBoardSelectable: canSelectItems,
  });

  const selectableItems = useMemo(() => (canSelectItems ? data : []), [canSelectItems, data]);

  useTableSelectableItems(selectableItems);

  const hasData = !!data?.length;

  const renderTableHeaders: Required<TableViewProps>['renderHeaders'] = useCallback(
    (ref) =>
      hasData &&
      isAboveMediumScreen && (
        <TableHeaders
          ref={ref}
          CustomFieldTableHeaderComponent={CustomFieldTableHeader}
          horizontalPadding={responsiveHorizontalPadding}
          topPos={tableHeaderTopPosition}
        />
      ),
    [hasData, isAboveMediumScreen, responsiveHorizontalPadding, tableHeaderTopPosition],
  );

  return (
    <TableViewProvider>
      {/* 'TableRow' is the legacy container test id every table view shares (see PrivateTableView) —
          kept verbatim so existing selectors keep matching. */}
      <TablePageContainer data-testid="TableRow">
        {isEmpty && typeof document !== 'undefined' ? (
          <PublicBoardNullState />
        ) : (
          <SelectableTableView
            canScrollHorizontally={isAboveMediumScreen && hasData}
            horizontalPadding={responsiveHorizontalPadding}
            renderHeaders={renderTableHeaders}
            rows={rows}
            scrollElementRef={scrollElementRef}
          />
        )}
      </TablePageContainer>
    </TableViewProvider>
  );
});

PublicBoardTableView.displayName = 'PublicBoardTableView';
